import { z } from "zod";
import type { ModuleType } from "@typedefs/interfaces";
import type { ModuleShared } from "@typedefs/modulesInterfaces";
import type { LandModule } from "@typedefs/modulesInterfaces";
import {
	landModuleSchema,
	mainModuleBaseSchema,
	moduleSharedSchema,
	nullableNumber,
} from "@typedefs/zodSchemas";

/** Zod schema for validating grassland module API responses - extends ModuleShared + LandModule */
export const grasslandResponseSchema = mainModuleBaseSchema
	.extend(moduleSharedSchema.shape)
	.extend(landModuleSchema.shape)
	.extend({
		description: nullableNumber.optional(),
		is_fire_used_start: z.boolean(),
		is_fire_used_w: z.boolean(),
		is_fire_used_wo: z.boolean(),
		fire_periodicity_start: nullableNumber.optional(),
		fire_periodicity_w: nullableNumber.optional(),
		fire_periodicity_wo: nullableNumber.optional(),
		fire_impact_start: nullableNumber.optional(),
		fire_impact_w: nullableNumber.optional(),
		fire_impact_wo: nullableNumber.optional(),
		yield_start: nullableNumber.optional(),
		yield_w: nullableNumber.optional(),
		yield_wo: nullableNumber.optional(),
		grassland_management_type_start: nullableNumber.optional(),
		grassland_management_type_w: nullableNumber.optional(),
		grassland_management_type_wo: nullableNumber.optional(),
		is_fire_used_thread: nullableNumber.optional(),
		fire_periodicity_thread: nullableNumber.optional(),
		fire_impact_thread: nullableNumber.optional(),
		yield_thread: nullableNumber.optional(),
		combustion_factor_t2_start: nullableNumber.optional(),
		combustion_factor_t2_w: nullableNumber.optional(),
		combustion_factor_t2_wo: nullableNumber.optional(),
		biomass_t2_start: nullableNumber.optional(),
		biomass_t2_w: nullableNumber.optional(),
		biomass_t2_wo: nullableNumber.optional(),
		agb_t2_start: nullableNumber.optional(),
		agb_t2_w: nullableNumber.optional(),
		agb_t2_wo: nullableNumber.optional(),
		// bgb_t2_* and *_rate_t2_* sent by API but excluded from form — display-only / computed
		bgb_t2_start: nullableNumber.optional(),
		bgb_t2_w: nullableNumber.optional(),
		bgb_t2_wo: nullableNumber.optional(),
		agb_rate_t2_start: nullableNumber.optional(),
		agb_rate_t2_w: nullableNumber.optional(),
		agb_rate_t2_wo: nullableNumber.optional(),
		bgb_rate_t2_start: nullableNumber.optional(),
		bgb_rate_t2_w: nullableNumber.optional(),
		bgb_rate_t2_wo: nullableNumber.optional(),
});

/** Inferred type from grasslandResponseSchema - use for validated API responses */
// export type GrasslandResponseInferred = z.infer<typeof grasslandResponseSchema>;

export interface GrasslandPayload extends ModuleShared, LandModule {
	id: number | null;

	is_fire_used_start: boolean;
	is_fire_used_w: boolean;
	is_fire_used_wo: boolean;
	fire_periodicity_start: number | null;
	fire_periodicity_w: number | null;
	fire_periodicity_wo: number | null;
	fire_impact_start: number | null;
	fire_impact_w: number | null;
	fire_impact_wo: number | null;
	yield_start: number | null;
	yield_w: number | null;
	yield_wo: number | null;
	area: number | null;
	land_use_type_thread: number | null;
	grassland_management_type_start: number | null;
	grassland_management_type_w: number | null;
	grassland_management_type_wo: number | null;
	is_fire_used_thread: number | null;
	fire_periodicity_thread: number | null;
	fire_impact_thread: number | null;
	yield_thread: number | null;
}

export interface GrasslandT2Payload {
	combustion_factor_t2_start: number | null;
	combustion_factor_t2_w: number | null;
	combustion_factor_t2_wo: number | null;
	biomass_t2_start: number | null;
	biomass_t2_w: number | null;
	biomass_t2_wo: number | null;
	agb_t2_start: number | null;
	agb_t2_w: number | null;
	agb_t2_wo: number | null;

	fmg_t2_start: null,
	fmg_t2_w: null,
	fmg_t2_wo: null,
	fi_t2_start: null,
	fi_t2_w: null,
	fi_t2_wo: null,
	flu_t2_start: null,
	flu_t2_w: null,
	flu_t2_wo: null,


	soc_t2_start: number | null;
	soc_t2_w: number | null;
	soc_t2_wo: number | null;
}

export interface GrasslandsT2 extends GrasslandT2Payload, Partial<GrasslandDefaults> { }

export interface ModuleFull {
	module: GrasslandPayload;
	tiertwo: GrasslandsT2;
}

export interface GrasslandResponse extends ModuleShared, LandModule {
	id: number;
	module_type: ModuleType;
	created_at: string;
	updated_at: string;
	description: number | null;

	is_fire_used_start: boolean;
	is_fire_used_w: boolean;
	is_fire_used_wo: boolean;
	fire_periodicity_start: number | null;
	fire_periodicity_w: number | null;
	fire_periodicity_wo: number | null;
	fire_impact_start: number | null;
	fire_impact_w: number | null;
	fire_impact_wo: number | null;
	yield_start: number | null;
	yield_w: number | null;
	yield_wo: number | null;
	area: number | null;
	land_use_type_thread: number | null;
	grassland_management_type_start: number | null;
	grassland_management_type_w: number | null;
	grassland_management_type_wo: number | null;
	is_fire_used_thread: number | null;
	fire_periodicity_thread: number | null;
	fire_impact_thread: number | null;
	yield_thread: number | null;

	combustion_factor_t2_start: number | null;
	combustion_factor_t2_w: number | null;
	combustion_factor_t2_wo: number | null;
	biomass_t2_start: number | null;
	biomass_t2_w: number | null;
	biomass_t2_wo: number | null;
	agb_t2_start: number | null;
	agb_t2_w: number | null;
	agb_t2_wo: number | null;

	// excluded from form — display-only / computed
	bgb_t2_start: number | null;
	bgb_t2_w: number | null;
	bgb_t2_wo: number | null;
	agb_rate_t2_start: number | null;
	agb_rate_t2_w: number | null;
	agb_rate_t2_wo: number | null;
	bgb_rate_t2_start: number | null;
	bgb_rate_t2_w: number | null;
	bgb_rate_t2_wo: number | null;
}

export interface GrasslandUpdatePayload {
	data: ModuleFull;
	activityId: number;
}

export interface GrasslandDefaults {
	combustion_factor_t2_start_default: number | null;
	combustion_factor_t2_w_default: number | null;
	combustion_factor_t2_wo_default: number | null;
	soc_t2_start_default: number | null,
	soc_t2_w_default: number | null,
	soc_t2_wo_default: number | null,
	biomass_t2_start_default: number | null;
	biomass_t2_w_default: number | null;
	biomass_t2_wo_default: number | null;
	agb_t2_start_default: number | null;
	agb_t2_w_default: number | null;
	agb_t2_wo_default: number | null;
}